import { useEffect, useState } from 'react';
import {
  Alert,
  Badge,
  Card,
  Group,
  SimpleGrid,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { Link } from 'react-router-dom';
import type { AuditSummary } from '@expeditor/shared';
import { auditApi } from '../../shared/api/audit';

/**
 * Главная страница «Проверка ПП РФ № 1317».
 *
 * Сводные счётчики по всем журналам + переход на детальные страницы.
 * Это эквивалент того, что увидит уполномоченный орган через oversight API.
 */
export function AuditSummaryPage() {
  const [s, setS] = useState<AuditSummary | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void auditApi
      .summary()
      .then(setS)
      .catch((e) => setError(e instanceof Error ? e.message : 'Не удалось загрузить'));
  }, []);

  return (
    <Stack>
      <Title order={2}>Проверка соответствия ПП РФ № 1317</Title>
      <Text c="dimmed">
        Read-only обзор всех данных, которые система собирает по требованиям п. 3 Постановления
        Правительства РФ № 1317 от 29.08.2025. Здесь видно ровно то, что увидит уполномоченный
        орган при проверке (этап 10 — отдельный oversight API).
      </Text>

      {error && <Alert color="red">{error}</Alert>}
      {!s && !error && <Text c="dimmed">Загрузка…</Text>}

      {s && (
        <>
          <Title order={4} mt="lg">
            Идентификация и события клиентов (п. 3 а)–д))
          </Title>
          <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }}>
            <StatCard
              title="Клиенты"
              clause="п.3 а)"
              count={s.clients}
              to="/audit/clients"
              hint="ФЛ / ИП / ЮЛ РФ / Иностранная орг."
            />
            <StatCard
              title="Регистрации"
              clause="п.3 б)"
              count={s.registrationEvents}
              to="/audit/registration-events"
              hint="IP, порты, UA, ЧП, payload"
            />
            <StatCard
              title="Входы в систему"
              clause="п.3 в)"
              count={s.authEvents}
              to="/audit/auth-events"
              extra={
                s.authFailures > 0 ? (
                  <Badge color="red">{s.authFailures} неуспешных</Badge>
                ) : null
              }
            />
            <StatCard
              title="Изменения профиля"
              clause="п.3 г)"
              count={s.profileChanges}
              to="/audit/profile-changes"
              hint="каждое поле = отдельная запись"
            />
            <StatCard
              title="Прекращения регистрации"
              clause="п.3 д)"
              count={s.deregistrations}
              to="/audit/deregistrations"
            />
          </SimpleGrid>

          <Title order={4} mt="lg">
            Договоры и привлекаемые исполнители (п. 3 е), ж))
          </Title>
          <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }}>
            <StatCard title="Поручения" clause="п.3 е)" count={s.orders} to="/audit/orders" />
            <StatCard title="Котировки" clause="п.3 е)" count={s.quotes} to="/audit/quotes" />
            <StatCard
              title="Договоры (контракты)"
              clause="п.3 е)"
              count={s.contracts}
              to="/audit/contracts"
            />
            <StatCard
              title="Привлекаемые исполнители"
              clause="п.3 ж)"
              count={0}
              to="/audit/contract-parties"
              hint="перевозчики, субподрядчики"
            />
          </SimpleGrid>

          <Title order={4} mt="lg">
            Перевозки, события груза и геолокация (п. 3 з), к))
          </Title>
          <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }}>
            <StatCard
              title="Перевозки (Shipment)"
              clause="п.3 з)"
              count={s.shipments}
              to="/audit/shipments"
            />
            <StatCard
              title="События груза"
              clause="п.3 з)"
              count={s.cargoEvents}
              to="/audit/cargo-events"
              hint="приём, погрузка, в пути, выгрузка, выдача"
            />
            <StatCard
              title="Точки геолокации"
              clause="п.3 к)"
              count={s.geoTrackPoints}
              to="/audit/geo-track"
            />
          </SimpleGrid>

          <Title order={4} mt="lg">
            Платежи и общий аудит (п. 3 и))
          </Title>
          <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }}>
            <StatCard
              title="Платежи"
              clause="п.3 и)"
              count={s.payments}
              to="/audit/payments"
              hint="MANUAL и ONEC_PUSH"
            />
            <StatCard
              title="AuditLog"
              clause="кросс-cutting"
              count={s.auditLogs}
              to="/audit/audit-logs"
              hint="все mutating-эндпоинты"
            />
          </SimpleGrid>

          <Alert color="blue" mt="lg" title="Что важно понимать проверяющему">
            <Stack gap="xs">
              <Text size="sm">
                • Все журналы (RegistrationEvent, AuthEvent, ProfileChangeEvent,
                DeregistrationEvent, CargoEvent, AuditLog) — <strong>append-only</strong>: запись
                нельзя править или удалить через приложение.
              </Text>
              <Text size="sm">
                • Идентификация клиента (п. 3 а) хранится в нормализованной форме с
                разделением по типу субъекта (ФЛ / ИП / ЮЛ РФ / иностранная орг.).
              </Text>
              <Text size="sm">
                • Прекращение регистрации (п. 3 д) — <strong>мягкая деактивация</strong> с
                сохранением всех данных. Физическое удаление невозможно.
              </Text>
              <Text size="sm">
                • При закрытии перевозки автоматически проставляется{' '}
                <code>Contract.endedAt</code> — стартует ретеншн 3 года.
              </Text>
              <Text size="sm">
                • На этапе 10 эти же данные будут отдаваться через отдельный read-only API{' '}
                <code>/oversight/v1/*</code> с авторизацией по{' '}
                <code>OVERSIGHT_API_KEY</code> + полное журналирование обращений
                в <code>OversightLog</code>.
              </Text>
            </Stack>
          </Alert>
        </>
      )}
    </Stack>
  );
}

function StatCard({
  title,
  clause,
  count,
  to,
  hint,
  extra,
}: {
  title: string;
  clause: string;
  count: number;
  to: string;
  hint?: string;
  extra?: React.ReactNode;
}) {
  return (
    <Card
      withBorder
      shadow="sm"
      component={Link}
      to={to}
      style={{ textDecoration: 'none', color: 'inherit' }}
    >
      <Group justify="space-between" mb={4}>
        <Badge variant="light" size="sm">
          {clause}
        </Badge>
        {extra}
      </Group>
      <Text fw={500}>{title}</Text>
      <Text fz="xl" fw={700} mt={4}>
        {count.toLocaleString('ru-RU')}
      </Text>
      {hint && (
        <Text size="xs" c="dimmed" mt={4}>
          {hint}
        </Text>
      )}
    </Card>
  );
}
